import {
  FC,
  useContext,
  useEffect,
  useId,
  useCallback,
  ComponentProps,
} from "react";
import { DialogContext } from "../Providers/DialogContext";

/**
 * Type helper for dialog components that resolve a value.
 * Add `resolves: TData` to the FC type to enable type inference in useDialog.
 *
 * @example
 * ```tsx
 * type MyDialogProps = { id: string };
 * export const MyDialog: DialogComponent<MyDialogProps, MyReturnType> = ({ id }) => {
 *   const { resolve } = useDialogState<MyReturnType>();
 *   // ...
 * };
 *
 * // Usage - TData is inferred as MyReturnType
 * const { openAsPromise } = useDialog(MyDialog);
 * const result = await openAsPromise({ id: "123" }); // result: MyReturnType
 * ```
 */
export type DialogComponent<TProps, TResolves = void> = FC<TProps> & {
  __resolves?: TResolves;
};

type InferResolves<T> =
  T extends DialogComponent<any, infer R>
    ? R
    : T extends FC<any>
      ? unknown
      : never;

export function useDialog<TDialog extends FC<any>>(
  dialog: TDialog,
): {
  open: (props: ComponentProps<TDialog>) => void;
  openAsPromise: (
    props: ComponentProps<TDialog>,
  ) => Promise<InferResolves<TDialog>>;
} {
  type TProps = ComponentProps<TDialog>;
  type TData = InferResolves<TDialog>;
  const id = useId();
  const { createDialog, removeDialog } = useContext(DialogContext);

  // Memoize the open function
  const open = useCallback(
    (props: TProps) => {
      createDialog(id, {
        Component: dialog as FC<Record<string, any>>,
        isOpen: true,
        props: props as Record<string, any>,
      });
    },
    [id, createDialog, dialog],
  );

  // Memoize the openAsPromise function
  const openAsPromise = useCallback(
    async (props: TProps): Promise<TData> => {
      return new Promise<TData>((resolve, reject) => {
        createDialog(id, {
          Component: dialog as FC<Record<string, any>>,
          props: props as Record<string, any>,
          resolve,
          reject,
          isOpen: true,
        });
      });
    },
    [id, createDialog, dialog],
  );

  useEffect(() => {
    return () => {
      removeDialog(id);
    };
  }, [id, removeDialog]);

  return { open, openAsPromise };
}
